import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import InvoiceIcOverrideModal from './InvoiceIcOverrideModal';

import InvoicePreview from './InvoicePreview';
import TuteeInvoiceAdjustmentModal from './TuteeInvoiceAdjustmentModal';
import IcSummaryModal from './IcSummaryModal';
import MonthYearNavigator from './MonthYearNavigator';
import { saveMonthYear, loadMonthYear } from '../utils/monthYearPersistence';
import { todaySGT } from '../utils/dateUtils';
import '../assets/TuteeInvoiceManagement.css';
import '../assets/PageHeader.css';
import '../assets/Pagination.css';

const TuteeInvoiceManagement = () => {
  // Get current user
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Date selection state - load from localStorage or use current month/year
  const savedMonthYear = loadMonthYear('tutee_invoice_management');
  const [selectedMonth, setSelectedMonth] = useState(savedMonthYear.month);
  const [selectedYear, setSelectedYear] = useState(savedMonthYear.year);

  // Status tab state
  const [activeStatusTab, setActiveStatusTab] = useState('all');

  // Invoice data state
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Filter and search state
  const [filters, setFilters] = useState({
    status: '',
    paymentStatus: '',
    financeIC: '',
    paymentIC: '',
    tutee: '',
    sortBy: 'invoiceNumber',
    sortOrder: 'asc'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Modal states
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);

  const [showInvoicePreviewModal, setShowInvoicePreviewModal] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [showMarkSentModal, setShowMarkSentModal] = useState(false);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [showReviseModal, setShowReviseModal] = useState(false);
  const [revisionReason, setRevisionReason] = useState('');
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [showIcSummaryModal, setShowIcSummaryModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoicePreviewData, setInvoicePreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Payment form state
  const [paymentData, setPaymentData] = useState({
    paymentDate: todaySGT(),
    paymentMethod: 'PayNow',
    notes: ''
  });

  // Total invoices count (no pagination)
  const [totalInvoices, setTotalInvoices] = useState(0);

  // Available Finance ICs
  const [availableFinanceICs, setAvailableFinanceICs] = useState([]);

  // Available Payment ICs (PayNow collectors)
  const [availablePaymentICs, setAvailablePaymentICs] = useState([]);

  // Stats state
  const [stats, setStats] = useState({
    totalFamilies: 0,
    totalInvoices: 0,
    totalRevenue: 0,
    icMatrix: { columns: [], rows: [], colTotals: [], grandTotal: 0 },
    statusBreakdown: {
      draft: 0,
      finalized: 0,
      sent: 0,
      paid: 0
    }
  });
  const [loadingStats, setLoadingStats] = useState(false);

  // Debounce search term — only trigger fetch 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load invoices when filters change
  useEffect(() => {
    const abortController = new AbortController();
    
    fetchInvoices(abortController.signal);
    fetchStats(abortController.signal);
    
    return () => abortController.abort();
  }, [selectedMonth, selectedYear, filters, activeStatusTab, debouncedSearchTerm]);

  // Load available Finance ICs + Payment ICs on mount
  useEffect(() => {
    fetchAvailableFinanceICs();
    fetchAvailablePaymentICs();
  }, []);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchInvoices = async (signal) => {
    setLoading(true);
    setError('');

    try {
      const params = {
        month: selectedMonth,
        year: selectedYear,
        ...filters
      };

      // Apply status filter based on active tab
      if (activeStatusTab !== 'all') {
        params.status = activeStatusTab;
      }

      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }

      const response = await adminApi.getTuteeInvoices(params, signal);
      
      // If this request was aborted, don't update state
      if (signal?.aborted) return;
      
      if (response.data.success) {
        // Backend returns data in response.data.data.invoices
        const invoiceData = response.data.data || response.data;
        const allInvoices = invoiceData.invoices || [];
        setInvoices(allInvoices);
        setTotalInvoices(allInvoices.length);
      } else {
        setError('Failed to load tutee invoices');
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      console.error('Error fetching tutee invoices:', error);
      if (!signal?.aborted) {
        setError(error.response?.data?.message || 'Failed to load tutee invoices');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  };

  const fetchStats = async (signal) => {
    setLoadingStats(true);

    try {
      const params = {
        month: selectedMonth,
        year: selectedYear
      };

      const response = await adminApi.getTuteeInvoices(params, signal);
      
      // If this request was aborted, don't update state
      if (signal?.aborted) return;
      
      if (response.data.success) {
        const allInvoices = response.data.data?.invoices || [];
        
        // Calculate stats
        // Count unique families by familyNumber (not by tutee ID)
        const uniqueFamilies = new Set(allInvoices.map(inv => inv.tutee?.familyNumber).filter(Boolean)).size;
        const totalRevenue = allInvoices.reduce((sum, inv) => sum + (inv.finalAmount || 0), 0);
        
        // Cross-tab matrix: rows = Finance IC, columns = Payment IC.
        // Each cell holds { total, collected, count }. "Unassigned" bucket on
        // each axis, plus row/column/grand totals.
        const FIC_NONE = '__no_fic__';
        const PIC_NONE = '__no_pic__';
        const ficRows = {}; // id -> { id, name }
        const picCols = {}; // id -> { id, name, number }
        const cell = {};    // `${ficId}|${picId}` -> { total, collected, count }

        const blank = () => ({ total: 0, collected: 0, count: 0 });

        allInvoices.forEach(inv => {
          const ficId = inv.financeIC?._id || FIC_NONE;
          const ficName = inv.financeIC?.name || 'Unassigned';
          const picName = inv.paymentIC?.paynowName || inv.paymentInstructions?.paynowName;
          const picId = inv.paymentIC?._id || (picName ? `name:${picName}` : PIC_NONE);
          const picNumber = inv.paymentIC?.paynowNumber || inv.paymentInstructions?.paynowNumber || '';

          if (!ficRows[ficId]) ficRows[ficId] = { id: ficId, name: ficName };
          if (!picCols[picId]) picCols[picId] = { id: picId, name: picName || 'Unassigned', number: picNumber };

          const k = `${ficId}|${picId}`;
          if (!cell[k]) cell[k] = blank();
          cell[k].total += inv.finalAmount || 0;
          cell[k].count += 1;
          if (inv.paymentStatus === 'paid') cell[k].collected += inv.finalAmount || 0;
        });

        // Order: named first (alpha), Unassigned last.
        const sortEntities = (arr, noneId) => arr.sort((a, b) => {
          if (a.id === noneId) return 1;
          if (b.id === noneId) return -1;
          return (a.name || '').localeCompare(b.name || '');
        });
        const columns = sortEntities(Object.values(picCols), PIC_NONE);
        const rows = sortEntities(Object.values(ficRows), FIC_NONE);

        const sumCells = (arr) => arr.reduce((acc, c) => ({
          total: acc.total + c.total,
          collected: acc.collected + c.collected,
          count: acc.count + c.count
        }), blank());

        const matrixRows = rows.map(r => {
          const cells = columns.map(c => cell[`${r.id}|${c.id}`] || blank());
          return { ...r, cells, rowTotal: sumCells(cells) };
        });
        const colTotals = columns.map((c, ci) => sumCells(matrixRows.map(r => r.cells[ci])));
        const grandTotal = sumCells(matrixRows.map(r => r.rowTotal));

        const icMatrix = { columns, rows: matrixRows, colTotals, grandTotal };

        // Status breakdown
        const statusBreakdown = {
          draft: allInvoices.filter(inv => inv.status === 'draft').length,
          finalized: allInvoices.filter(inv => inv.status === 'finalized').length,
          sent: allInvoices.filter(inv => inv.status === 'sent').length,
          paid: allInvoices.filter(inv => inv.status === 'paid').length
        };
        
        setStats({
          totalFamilies: uniqueFamilies,
          totalInvoices: allInvoices.length,
          totalRevenue,
          icMatrix,
          statusBreakdown
        });
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      console.error('Error fetching stats:', error);
    } finally {
      if (!signal?.aborted) {
        setLoadingStats(false);
      }
    }
  };

  const fetchAvailableFinanceICs = async () => {
    try {
      const response = await adminApi.getEligibleFinanceICs();
      if (response.data.success) {
        setAvailableFinanceICs(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching Finance ICs:', error);
    }
  };

  const fetchAvailablePaymentICs = async () => {
    try {
      const response = await adminApi.getPaymentICs();
      if (response.data.success) {
        setAvailablePaymentICs(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching Payment ICs:', error);
    }
  };

  // Handle month/year changes
  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    saveMonthYear('tutee_invoice_management', month, selectedYear);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    saveMonthYear('tutee_invoice_management', selectedMonth, year);
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  // Handle sorting
  const handleSort = (field) => {
    const newOrder = filters.sortBy === field && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: newOrder
    }));
  };

  // Handle bulk assignment for all invoices
  const handleBulkAssignAllFinanceIC = () => {
    if (invoices.length === 0) {
      setError('No invoices available to assign');
      return;
    }
    
    // Only pass finalized/sent/paid invoices - drafts get Finance IC assigned at finalization
    const assignableInvoices = invoices.filter(inv => inv.status !== 'draft');
    if (assignableInvoices.length === 0) {
      setError('No finalized invoices to reassign. Finalize invoices first — Finance IC is assigned automatically at finalization.');
      return;
    }
    
    setShowAssignmentModal(true);
  };

  // Handle discount application
  const handleApplyDiscount = async (invoiceId, discountData) => {
    try {
      const response = await adminApi.updateTuteeInvoice(invoiceId, {
        action: 'apply_discount',
        discount: discountData
      });

      if (response.data.success) {
        fetchInvoices(); // Refresh the list
      } else {
        setError('Failed to apply discount');
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      setError(error.response?.data?.message || 'Failed to apply discount');
    }
  };

  // Handle adjustment application
  const handleApplyAdjustment = async (invoiceId, adjustmentData) => {
    try {
      const response = await adminApi.updateTuteeInvoice(invoiceId, {
        action: 'apply_adjustment',
        adjustment: adjustmentData
      });

      if (response.data.success) {
        fetchInvoices(); // Refresh the list
      } else {
        setError('Failed to apply adjustment');
      }
    } catch (error) {
      console.error('Error applying adjustment:', error);
      setError(error.response?.data?.message || 'Failed to apply adjustment');
    }
  };

  // Handle invoice deletion
  const handleDeleteInvoice = async (invoiceId, invoiceNumber) => {
    if (!window.confirm(`Are you sure you want to delete invoice ${invoiceNumber}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await adminApi.deleteTuteeInvoice(invoiceId);

      if (response.data.success) {
        fetchInvoices(); // Refresh the list
      } else {
        setError('Failed to delete invoice');
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      setError(error.response?.data?.message || 'Failed to delete invoice');
    }
  };

  // Handle view invoice
  const handleViewInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoicePreviewModal(true);
    setLoadingPreview(true);
    
    try {
      // Fetch full invoice details to ensure tutor data is populated
      const response = await adminApi.getTuteeInvoiceById(invoice._id);
      
      if (response.data.success) {
        const fullInvoice = response.data.data;
        
        // Transform lessons with proper tutor data
        const transformedLessons = (fullInvoice.lessons || []).map(lesson => ({
          ...lesson,
          tuteeRate: lesson.rate,
          tutor: lesson.tutor || { name: 'Unknown' },
          date: lesson.date
        }));
        
        const transformedData = {
          lessons: transformedLessons,
          adjustments: fullInvoice.adjustments || [],
          recurringPayments: [],
          discounts: fullInvoice.discounts || [],
          totals: {
            lessonEarnings: fullInvoice.subtotal || 0,
            adjustmentTotal: (fullInvoice.adjustments || []).reduce((sum, adj) => sum + adj.amount, 0),
            discountTotal: (fullInvoice.discounts || []).reduce((sum, disc) => sum + disc.amount, 0),
            recurringTotal: 0,
            finalAmount: fullInvoice.finalAmount || 0
          },
          lessonEarnings: fullInvoice.subtotal || 0,
          subtotal: fullInvoice.subtotal || 0
        };
        
        setInvoicePreviewData(transformedData);
      } else {
        setError('Failed to load invoice details');
      }
    } catch (error) {
      console.error('Error preparing invoice preview:', error);
      setError('Failed to load invoice details');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleAssignmentComplete = () => {
    setShowAssignmentModal(false);
    setSelectedInvoice(null);
    fetchInvoices();
  };

  // Handle finalize invoice
  const handleFinalizeInvoice = async () => {
    if (!selectedInvoice) return;

    setActionLoading(true);
    setError('');

    try {
      const response = await adminApi.finalizeTuteeInvoice(selectedInvoice._id);
      
      if (response.data.success) {
        setSuccessMessage(`Invoice ${selectedInvoice.invoiceNumber} finalized successfully. Amount is now locked.`);
        setShowFinalizeModal(false);
        setSelectedInvoice(null);
        fetchInvoices();
        
        // Check if there are profit sharer adjustments that need attention
        if (response.data.profitSharerAdjustments && response.data.profitSharerAdjustments.length > 0) {
          const adjustments = response.data.profitSharerAdjustments;
          const adjustmentSummary = adjustments.map(adj => 
            `${adj.profitSharer.name}: ${adj.difference > 0 ? '+' : ''}$${adj.difference.toFixed(2)}`
          ).join('\n');
          
          alert(`⚠️ Profit Sharer Adjustment Required\n\nThis invoice contains backdated lessons that affect already-paid profit sharer commissions:\n\n${adjustmentSummary}\n\nPlease review the pending adjustments in the alert at the top of the page.`);
        }
      } else {
        setError('Failed to finalize invoice');
      }
    } catch (error) {
      console.error('Error finalizing invoice:', error);
      setError(error.response?.data?.message || 'Failed to finalize invoice');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle mark as sent
  const handleMarkAsSent = async () => {
    if (!selectedInvoice) return;

    setActionLoading(true);
    setError('');

    try {
      const response = await adminApi.markTuteeInvoiceAsSent(selectedInvoice._id);
      
      if (response.data.success) {
        setSuccessMessage(`Invoice ${selectedInvoice.invoiceNumber} marked as sent successfully.`);
        setShowMarkSentModal(false);
        setSelectedInvoice(null);
        fetchInvoices();
      } else {
        setError('Failed to mark invoice as sent');
      }
    } catch (error) {
      console.error('Error marking invoice as sent:', error);
      setError(error.response?.data?.message || 'Failed to mark invoice as sent');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle mark as paid. mode: 'verify' sends the Payment IC a verify request
  // (invoice stays unpaid); 'paid' marks it paid now and notifies the IC.
  const handleMarkAsPaid = async (mode = 'paid') => {
    if (!selectedInvoice) return;

    setActionLoading(true);
    setError('');

    try {
      const payload = mode === 'verify' ? { mode } : { mode, ...paymentData };
      const response = await adminApi.markTuteeInvoiceAsPaid(selectedInvoice._id, payload);

      if (response.data.success) {
        if (response.data.warning) {
          setSuccessMessage(response.data.warning);
        } else if (mode === 'verify') {
          setSuccessMessage(`Verification request sent to the Payment IC for ${selectedInvoice.invoiceNumber}. Invoice stays unpaid until they confirm.`);
        } else {
          setSuccessMessage(`Invoice ${selectedInvoice.invoiceNumber} marked as paid.${response.data.icNotified ? ' Payment IC notified.' : ' (Payment IC not notified on WhatsApp.)'}`);
        }
        setShowMarkPaidModal(false);
        setSelectedInvoice(null);
        setPaymentData({
          paymentDate: todaySGT(),
          paymentMethod: 'PayNow',
          notes: ''
        });
        fetchInvoices();
        fetchStats(); // Refresh the Finance IC / Payment IC summary matrix with updated collected/outstanding numbers
      } else {
        setError('Failed to update invoice');
      }
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      setError(error.response?.data?.message || 'Failed to update invoice');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle revise invoice (sent/finalized → new draft with latest lessons)
  const handleReviseInvoice = async () => {
    if (!selectedInvoice || !revisionReason.trim()) return;

    setActionLoading(true);
    setError('');

    try {
      const response = await adminApi.reviseTuteeInvoice(selectedInvoice._id, revisionReason.trim());

      if (response.data.success) {
        const invoice = response.data.data?.invoice;
        setSuccessMessage(
          response.data.message ||
          `Invoice reset to draft with latest lessons. Review and re-finalize when ready.`
        );
        setShowReviseModal(false);
        setSelectedInvoice(null);
        setRevisionReason('');
        fetchInvoices();
        fetchStats();
      } else {
        setError('Failed to create revision');
      }
    } catch (error) {
      console.error('Error revising invoice:', error);
      setError(error.response?.data?.message || 'Failed to create revision');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle download PDF
  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await adminApi.downloadTuteeInvoicePDF(invoice._id);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Use filename from backend Content-Disposition header, or generate standardized one
      const contentDisposition = response.headers['content-disposition'];
      const tuteeCode = invoice.tutee?.code ? invoice.tutee.code.toUpperCase().replace(/[^A-Z0-9\-]/g, '_') + '_' : '';
      let filename = `invoice_tutee_${tuteeCode}${invoice.tutee.name.toLowerCase().replace(/[^a-z0-9]/g, '_')}_${invoice.month.toString().padStart(2, '0')}_${invoice.year}.pdf`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Failed to download PDF');
    }
  };

  // Finalize progress state
  const [finalizeProgress, setFinalizeProgress] = useState(null);
  // { total, done, success, failed, items: [{invoiceNumber, status, error}], finished }

  // Handle mark all as finalized
  const handleMarkAllAsFinalized = async () => {
    const draftInvoices = invoices.filter(inv => inv.status === 'draft');
    
    if (draftInvoices.length === 0) {
      setError('No draft invoices to finalize');
      return;
    }

    if (!window.confirm(`Are you sure you want to finalize ${draftInvoices.length} draft invoice(s)? This will lock their amounts and lessons.`)) {
      return;
    }

    setActionLoading(true);
    setError('');

    // Open progress popup in indeterminate state (single bulk request).
    setFinalizeProgress({
      total: draftInvoices.length,
      done: 0,
      success: 0,
      failed: 0,
      items: [],
      finished: false
    });

    try {
      // Single bulk call: the backend finalizes all month drafts and runs the
      // expensive backdated profit-share check ONCE, so this is far faster than
      // the old 30-sequential-requests loop.
      const response = await adminApi.finalizeAllDraftInvoices(selectedMonth, selectedYear);
      const data = response.data?.data || { total: draftInvoices.length, success: 0, failed: 0, results: [] };
      const results = data.results || [];

      setFinalizeProgress({
        total: data.total ?? draftInvoices.length,
        done: results.length || data.total || draftInvoices.length,
        success: data.success || 0,
        failed: data.failed || 0,
        items: results.map(r => ({
          invoiceNumber: r.invoiceNumber,
          status: r.status === 'ok' ? 'ok' : (r.status === 'skipped' ? 'ok' : 'error'),
          error: r.error
        })),
        finished: true
      });

      if ((data.success || 0) > 0) {
        fetchInvoices();
        fetchStats();
      }
    } catch (error) {
      console.error('Error finalizing invoices:', error);
      setError(error.response?.data?.message || 'Failed to finalize invoices');
      setFinalizeProgress(prev => ({ ...prev, finished: true }));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle refresh all draft invoices
  const handleRefreshAllDrafts = async () => {
    if (!window.confirm(`Refresh all draft invoices for ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}? This will recalculate amounts based on current lessons.`)) {
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      const response = await adminApi.refreshAllDraftInvoices(selectedMonth, selectedYear);
      
      if (response.data.success) {
        // Backend returns a ready-made summary (created / updated / unchanged /
        // removed / errors). Use it directly so new invoices and revived ones
        // are reflected accurately.
        setSuccessMessage(response.data.message || 'Draft invoices refreshed.');
        
        fetchInvoices();
        fetchStats();
      } else {
        setError('Failed to refresh draft invoices');
      }
    } catch (error) {
      console.error('Error refreshing draft invoices:', error);
      setError(error.response?.data?.message || 'Failed to refresh draft invoices');
    } finally {
      setActionLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `${amount.toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-SG', {
      timeZone: 'Asia/Singapore',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'draft': return 'status-badge status-draft';
      case 'finalized': return 'status-badge status-finalized';
      case 'sent': return 'status-badge status-sent';
      case 'paid': return 'status-badge status-paid';
      case 'disputed': return 'status-badge status-disputed';
      case 'resolved': return 'status-badge status-resolved';
      case 'revised': return 'status-badge status-revised';
      case 'pending': return 'status-badge status-pending';
      default: return 'status-badge';
    }
  };

  // Badge class for an invoice, accounting for the "payment indicated" sub-state
  const getStatusBadgeClassForInvoice = (invoice) => {
    if (invoice.status === 'sent' && invoice.paymentIndicated && invoice.paymentStatus !== 'paid') {
      return 'status-badge status-payment-indicated';
    }
    return getStatusBadgeClass(invoice.status);
  };

  // Get status display text
  const getStatusDisplayText = (invoice) => {
    const statusText = invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1);
    
    // Add additional context for certain statuses
    if (invoice.status === 'draft') {
      return 'Draft (Auto-updating)';
    } else if (invoice.status === 'finalized') {
      return 'Finalized (Locked)';
    } else if (invoice.status === 'sent') {
      // Parent tapped "I've paid" but the Payment IC hasn't verified yet.
      return invoice.paymentIndicated && invoice.paymentStatus !== 'paid'
        ? 'Sent (Payment Indicated)'
        : 'Sent (Awaiting Payment)';
    } else if (invoice.status === 'revised' && invoice.revisedFrom) {
      return `Revised (v${invoice.version})`;
    }
    
    return statusText;
  };

  // Get status timestamp
  const getStatusTimestamp = (invoice) => {
    switch (invoice.status) {
      case 'finalized':
        return invoice.finalizedAt;
      case 'sent':
        return invoice.sentAt;
      case 'paid':
        return invoice.paymentDate;
      default:
        return null;
    }
  };

  // Get payment status badge class
  const getPaymentStatusBadgeClass = (paymentStatus) => {
    switch (paymentStatus) {
      case 'unpaid': return 'payment-status-badge payment-unpaid';
      case 'paid': return 'payment-status-badge payment-paid';
      case 'partial': return 'payment-status-badge payment-partial';
      default: return 'payment-status-badge';
    }
  };

  // Format payment status display text
  const formatPaymentStatus = (invoice) => {
    if (invoice.paymentStatus === 'paid' && invoice.paymentDate) {
      const paidDate = new Date(invoice.paymentDate).toLocaleDateString('en-SG', {
        timeZone: 'Asia/Singapore',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
      return `Paid on ${paidDate}`;
    }
    // Capitalize first letter for all statuses
    return invoice.paymentStatus.charAt(0).toUpperCase() + invoice.paymentStatus.slice(1);
  };

  // Generate months array
  const months = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2024, i).toLocaleDateString('en-SG', { month: 'long' })
  }));

  // Generate years array
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - 2 + i);

  return (
    <div className="tutee-invoice-management">
      <div className="page-header with-background">
        <h1 className="page-header-title">Tutee Invoice Management</h1>
        <p className="page-header-subtitle">Manage invoices sent to parents for tutee lessons</p>
      </div>

      {/* Controls Section - Month/Year Navigator */}
      <div className="management-controls">
        <MonthYearNavigator
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onMonthChange={handleMonthChange}
          onYearChange={handleYearChange}
        />
      </div>

      {user?.role === 'admin' && (
        <IcSummaryModal
          isOpen={showIcSummaryModal}
          onClose={() => setShowIcSummaryModal(false)}
          paymentICs={availablePaymentICs}
          month={selectedMonth}
          year={selectedYear}
        />
      )}

      {/* Stats Cards */}
      <div className="stats-section">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#dbeafe' }}>
              <svg width="24" height="24" viewBox="0 0 20 20" fill="#3b82f6">
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Families</div>
              <div className="stat-value">{loadingStats ? '...' : stats.totalFamilies}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#fef3c7' }}>
              <svg width="24" height="24" viewBox="0 0 20 20" fill="#f59e0b">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Invoices</div>
              <div className="stat-value">{loadingStats ? '...' : stats.totalInvoices}</div>
              <div className="stat-breakdown">
                Draft: {stats.statusBreakdown.draft} | Finalized: {stats.statusBreakdown.finalized} | Sent: {stats.statusBreakdown.sent} | Paid: {stats.statusBreakdown.paid}
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#d1fae5' }}>
              <svg width="24" height="24" viewBox="0 0 20 20" fill="#10b981">
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Revenue</div>
              <div className="stat-value">${loadingStats ? '...' : stats.totalRevenue.toFixed(2)}</div>
            </div>
          </div>
        </div>

        {/* Finance IC Breakdown */}
        {stats.icMatrix.rows.length > 0 && (() => {
          // Money formatter: no cents when whole, keeps it compact.
          const money = (n) => {
            const v = Number(n) || 0;
            return v % 1 === 0 ? `$${v.toLocaleString()}` : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          };
          // Renders one cell: total on top, a thin collected/due progress bar,
          // and a compact status line — only showing what's relevant.
          const renderCell = (c, extraClass = '') => {
            if (!c || c.count === 0) return <span className="ic-matrix-zero">–</span>;
            const outstanding = Math.max(0, c.total - c.collected);
            const pct = c.total > 0 ? Math.round((c.collected / c.total) * 100) : 0;
            const fullyPaid = outstanding < 0.001;
            return (
              <div className={`ic-matrix-cell ${extraClass}`}>
                <span className="ic-mc-total">{money(c.total)}</span>
                <span className="ic-mc-bar" title={`${pct}% collected`}>
                  <span className="ic-mc-bar-fill" style={{ width: `${pct}%` }} />
                </span>
                {fullyPaid ? (
                  <span className="ic-mc-status ic-mc-done">✓ Fully collected</span>
                ) : (
                  <span className="ic-mc-status">
                    <span className="ic-mc-due">{money(outstanding)} due</span>
                    {c.collected > 0.001 && <span className="ic-mc-got"> · {money(c.collected)} in</span>}
                  </span>
                )}
                <span className="ic-mc-count">{c.count} invoice{c.count !== 1 ? 's' : ''}</span>
              </div>
            );
          };
          return (
            <div className="ic-matrix-panel">
              <h3>Payment IC × Finance IC Breakdown</h3>
              <div className="ic-matrix-scroll">
                <table className="ic-matrix-table">
                  <thead>
                    <tr>
                      <th className="ic-matrix-corner">
                        <span className="ic-corner-row">Finance IC ↓</span>
                        <span className="ic-corner-col">Payment IC →</span>
                      </th>
                      {stats.icMatrix.columns.map(col => (
                        <th key={col.id}>
                          {col.name}
                          {col.number && <span className="ic-matrix-sub"> {col.number}</span>}
                        </th>
                      ))}
                      <th className="ic-matrix-total-col">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.icMatrix.rows.map(row => (
                      <tr key={row.id}>
                        <td className="ic-matrix-rowhead">{row.name}</td>
                        {row.cells.map((c, ci) => (
                          <td key={ci}>{renderCell(c)}</td>
                        ))}
                        <td className="ic-matrix-total-col">{renderCell(row.rowTotal, 'is-total')}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="ic-matrix-rowhead">Total</td>
                      {stats.icMatrix.colTotals.map((c, ci) => (
                        <td key={ci} className="ic-matrix-total-col">{renderCell(c, 'is-total')}</td>
                      ))}
                      <td className="ic-matrix-grand">{renderCell(stats.icMatrix.grandTotal, 'is-total')}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Error Message */}
      {error && <ErrorMessage message={error} />}

      {/* Success Message */}
      {successMessage && (
        <div className="success-message" style={{
          padding: '12px 16px',
          backgroundColor: '#d1fae5',
          border: '1px solid #6ee7b7',
          borderRadius: '6px',
          color: '#065f46',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          {successMessage}
        </div>
      )}

      {/* Status Tabs */}
      <div className="status-tabs">
        <div className="status-tabs-left">
          <button
            className={`status-tab ${activeStatusTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveStatusTab('all')}
          >
            All Invoices
          </button>
          <button
            className={`status-tab ${activeStatusTab === 'draft' ? 'active' : ''}`}
            onClick={() => setActiveStatusTab('draft')}
          >
            Draft
          </button>
          <button
            className={`status-tab ${activeStatusTab === 'finalized' ? 'active' : ''}`}
            onClick={() => setActiveStatusTab('finalized')}
          >
            Finalized
          </button>
          <button
            className={`status-tab ${activeStatusTab === 'sent' ? 'active' : ''}`}
            onClick={() => setActiveStatusTab('sent')}
          >
            Sent
          </button>
          <button
            className={`status-tab ${activeStatusTab === 'paid' ? 'active' : ''}`}
            onClick={() => setActiveStatusTab('paid')}
          >
            Paid
          </button>
        </div>
        
        <div className="status-tabs-right">
          <button
            className="btn-refresh-drafts"
            onClick={handleRefreshAllDrafts}
            disabled={actionLoading}
            title="Sync all draft invoices with current lessons — also creates missing invoices for tutees who have lessons but no draft yet"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
            </svg>
            Refresh Drafts ({stats.statusBreakdown.draft})
          </button>
          
          <button
            className="btn-finalize-all"
            onClick={handleMarkAllAsFinalized}
            disabled={actionLoading || stats.statusBreakdown.draft === 0}
            title="Finalize all draft invoices"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Mark All as Finalized ({stats.statusBreakdown.draft})
          </button>

          {user?.role === 'admin' && (
            <button
              className="btn-finalize-all"
              onClick={() => setShowIcSummaryModal(true)}
              title="Send the monthly payment summary to a Payment IC via WhatsApp"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2z"/>
              </svg>
              Send Summary to Payment IC
            </button>
          )}
        </div>
      </div>

      {/* Invoice Table with Filters and Search */}
      <div className="invoice-table-section-v2">
          <div className="table-header-v2">
            <h2>
              Tutee Invoices 
              {totalInvoices > 0 && (
                <span className="invoice-count">({totalInvoices} total)</span>
              )}
            </h2>
            <button
              className="btn assign-all-btn-v2"
              onClick={handleBulkAssignAllFinanceIC}
              disabled={invoices.length === 0}
              title="Override Finance IC / Payment IC for this month's finalized invoices"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
              </svg>
              Assign IC
            </button>
          </div>

          {/* Filters and Search Row */}
          <div className="table-filters-v2">
            <div className="filter-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by tutee name, invoice number, or parent name/phone..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="search-input"
                style={{ padding: '8px 10px', border: '1px solid #ddd', fontSize: '14px', width: '100%' }}
              />
            </div>

            <div className="filter-group">
              <label>Status:</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="assigned">Assigned</option>
                <option value="paid">Paid</option>
                <option value="disputed">Disputed</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Payment Status:</label>
              <select
                value={filters.paymentStatus}
                onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
              >
                <option value="">All Payment Statuses</option>
                <option value="unpaid">Unpaid</option>
                <option value="paid">Paid</option>
                <option value="partial">Partial</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Finance IC:</label>
              <select
                value={filters.financeIC}
                onChange={(e) => handleFilterChange('financeIC', e.target.value)}
              >
                <option value="">All Finance ICs</option>
                {availableFinanceICs.map(ic => (
                  <option key={ic._id} value={ic._id}>
                    {ic.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Payment IC:</label>
              <select
                value={filters.paymentIC}
                onChange={(e) => handleFilterChange('paymentIC', e.target.value)}
              >
                <option value="">All Payment ICs</option>
                {availablePaymentICs.map(ic => (
                  <option key={ic._id} value={ic._id}>
                    {ic.paynowName} ({ic.paynowNumber})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="table-loading-overlay">
              <LoadingSpinner />
            </div>
          ) : invoices.length === 0 ? (
            <div className="no-invoices">
              <p>No tutee invoices found for the selected criteria.</p>
            </div>
          ) : (
            <>
              <div className="invoice-table-container-v2">
                <table className="invoice-table-v2">
                  <thead>
                    <tr>
                      <th 
                        className="sortable-v2"
                        onClick={() => handleSort('invoiceNumber')}
                      >
                        Invoice #
                        {filters.sortBy === 'invoiceNumber' && (
                          <span className="sort-indicator-v2">
                            {filters.sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        className="sortable-v2"
                        onClick={() => handleSort('parent.name')}
                      >
                        Parent
                        {filters.sortBy === 'parent.name' && (
                          <span className="sort-indicator-v2">
                            {filters.sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        className="sortable-v2"
                        onClick={() => handleSort('tutee.name')}
                      >
                        Tutee
                        {filters.sortBy === 'tutee.name' && (
                          <span className="sort-indicator-v2">
                            {filters.sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        className="sortable-v2"
                        onClick={() => handleSort('finalAmount')}
                      >
                        Amount
                        {filters.sortBy === 'finalAmount' && (
                          <span className="sort-indicator-v2">
                            {filters.sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        className="sortable-v2"
                        onClick={() => handleSort('paymentStatus')}
                      >
                        Payment
                        {filters.sortBy === 'paymentStatus' && (
                          <span className="sort-indicator-v2">
                            {filters.sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        className="sortable-v2"
                        onClick={() => handleSort('paymentIC.paynowName')}
                      >
                        Payment IC
                        {filters.sortBy === 'paymentIC.paynowName' && (
                          <span className="sort-indicator-v2">
                            {filters.sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        className="sortable-v2"
                        onClick={() => handleSort('paymentDueDate')}
                      >
                        Due Date
                        {filters.sortBy === 'paymentDueDate' && (
                          <span className="sort-indicator-v2">
                            {filters.sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        className="sortable-v2"
                        onClick={() => handleSort('status')}
                      >
                        Status
                        {filters.sortBy === 'status' && (
                          <span className="sort-indicator-v2">
                            {filters.sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(invoice => (
                      <tr key={invoice._id}>
                        <td>
                          <span className="invoice-number-v2">{invoice.invoiceNumber}</span>
                        </td>
                        <td>
                          <div className="parent-info-v2">
                            <span className="parent-name-v2">{invoice.tutee?.parent?.name || invoice.parent?.name}</span>
                            <span className="parent-email-v2">{invoice.tutee?.parent?.phone || invoice.parent?.phone}</span>
                          </div>
                        </td>
                        <td>
                          <div className="tutee-info-v2">
                            <span className="tutee-name-v2">{invoice.tutee?.name}</span>
                            <span className="tutee-code-v2">{invoice.tutee?.code}</span>
                          </div>
                        </td>
                        <td>
                          <span className="invoice-amount-v2">${formatCurrency(invoice.finalAmount)}</span>
                        </td>
                        <td>
                          <span className={`payment-status-badge-v2 payment-${invoice.paymentStatus}`}>
                            {formatPaymentStatus(invoice)}
                          </span>
                        </td>
                        <td>
                          {(() => {
                            // Override = invoice IC differs from the tutee's current default.
                            const invPicId = invoice.paymentIC?._id || invoice.paymentIC;
                            const defPicId = invoice.tutee?.defaultPaymentIC?._id || invoice.tutee?.defaultPaymentIC;
                            const picOverridden = invPicId && defPicId && String(invPicId) !== String(defPicId);
                            const invFicId = invoice.financeIC?._id || invoice.financeIC;
                            const defFicId = invoice.tutee?.defaultFinanceIC?._id || invoice.tutee?.defaultFinanceIC;
                            const ficOverridden = invFicId && defFicId && String(invFicId) !== String(defFicId);
                            const paynowName = invoice.paymentIC?.paynowName || invoice.paymentInstructions?.paynowName;
                            const paynowNumber = invoice.paymentIC?.paynowNumber || invoice.paymentInstructions?.paynowNumber;
                            return paynowName ? (
                              <div className="finance-ic-info-v2">
                                <span className="finance-ic-name-v2">
                                  {paynowName}
                                  {picOverridden && <span className="override-badge" title="Overridden for this invoice — differs from the tutee's default Payment IC">Overridden</span>}
                                </span>
                                {paynowNumber && (
                                  <span className="assignment-date-v2">{paynowNumber}</span>
                                )}
                                {invoice.financeIC?.name && (
                                  <span className="assignment-date-v2">
                                    Finance IC: {invoice.financeIC.name}
                                    {ficOverridden && <span className="override-badge" title="Finance IC overridden for this invoice">Overridden</span>}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="no-assignment-v2">No Payment IC</span>
                            );
                          })()}
                        </td>
                        <td>
                          {invoice.status !== 'draft' && invoice.paymentDueDate ? (
                            <span className={`due-date-v2 ${new Date(invoice.paymentDueDate) < new Date() ? 'overdue' : ''}`}>
                              {formatDate(invoice.paymentDueDate)}
                            </span>
                          ) : (
                            <span className="no-due-date-v2">-</span>
                          )}
                        </td>
                        <td>
                          <div className="status-with-date">
                            <span className={getStatusBadgeClassForInvoice(invoice)}>
                              {getStatusDisplayText(invoice)}
                            </span>
                            {getStatusTimestamp(invoice) && (
                              <span className="status-date-v2">
                                {formatDate(getStatusTimestamp(invoice))}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="action-buttons-v2">
                            {/* ADMIN VIEW */}
                            {user?.role === 'admin' && (
                              <>
                                {/* View button - always available */}
                                <button
                                  className="btn-icon-v2"
                                  onClick={() => handleViewInvoice(invoice)}
                                  title="View Invoice"
                                >
                                  <svg viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                  </svg>
                                </button>

                                {/* Manage Adjustments button - always available for admin */}
                                <button
                                  className="btn-icon-v2"
                                  onClick={() => {
                                    setSelectedInvoice(invoice);
                                    setShowAdjustmentModal(true);
                                  }}
                                  title="Manage Adjustments"
                                >
                                  <svg viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                  </svg>
                                </button>

                                {/* Finalize button - only for draft status */}
                                {invoice.status === 'draft' && (
                                  <button
                                    className="btn-icon-v2 btn-finalize"
                                    onClick={() => {
                                      setSelectedInvoice(invoice);
                                      setShowFinalizeModal(true);
                                    }}
                                    title="Mark as Finalized"
                                  >
                                    <svg viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                )}

                                {/* Download PDF button - for finalized, sent, paid */}
                                {['finalized', 'sent', 'paid'].includes(invoice.status) && (
                                  <button
                                    className="btn-icon-v2 btn-download"
                                    onClick={() => handleDownloadPDF(invoice)}
                                    title="Download PDF"
                                  >
                                    <svg viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                )}

                                {/* Mark as Paid button - admin fallback (e.g. WhatsApp verify unavailable) */}
                                {['finalized', 'sent'].includes(invoice.status) && invoice.paymentStatus !== 'paid' && (
                                  <button
                                    className="btn-icon-v2 btn-paid"
                                    onClick={() => {
                                      setSelectedInvoice(invoice);
                                      setShowMarkPaidModal(true);
                                    }}
                                    title="Mark as Paid"
                                  >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <line x1="12" y1="1" x2="12" y2="23"/>
                                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                                    </svg>
                                  </button>
                                )}

                                {/* Revise button - for finalized/sent invoices to pull in new tutor submissions */}
                                {['finalized', 'sent'].includes(invoice.status) && (
                                  <button
                                    className="btn-icon-v2 btn-revise"
                                    onClick={() => {
                                      setSelectedInvoice(invoice);
                                      setRevisionReason('');
                                      setShowReviseModal(true);
                                    }}
                                    title="Revise Invoice (pull in new lessons)"
                                  >
                                    <svg viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>


            </>
          )}
        </div>

      {/* Hidden - V2 TABLE WAS FOR COMPARISON ONLY */}
      {false && !loading && invoices.length > 0 && (
        <div className="invoice-table-section-v2">
          <div className="table-header-v2">
            <h2>V2 - Minimal Design (For Comparison)</h2>
          </div>

          <div className="invoice-table-container-v2">
            <table className="invoice-table-v2">
              <thead>
                <tr>
                  <th 
                    className="sortable-v2"
                    onClick={() => handleSort('invoiceNumber')}
                  >
                    Invoice #
                    {filters.sortBy === 'invoiceNumber' && (
                      <span className="sort-indicator-v2">
                        {filters.sortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className="sortable-v2"
                    onClick={() => handleSort('tutee.name')}
                  >
                    Tutee
                    {filters.sortBy === 'tutee.name' && (
                      <span className="sort-indicator-v2">
                        {filters.sortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className="sortable-v2"
                    onClick={() => handleSort('parent.name')}
                  >
                    Parent
                    {filters.sortBy === 'parent.name' && (
                      <span className="sort-indicator-v2">
                        {filters.sortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className="sortable-v2"
                    onClick={() => handleSort('finalAmount')}
                  >
                    Amount
                    {filters.sortBy === 'finalAmount' && (
                      <span className="sort-indicator-v2">
                        {filters.sortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className="sortable-v2"
                    onClick={() => handleSort('paymentStatus')}
                  >
                    Payment
                    {filters.sortBy === 'paymentStatus' && (
                      <span className="sort-indicator-v2">
                        {filters.sortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th>
                    Payment IC
                  </th>
                  <th 
                    className="sortable-v2"
                    onClick={() => handleSort('paymentDueDate')}
                  >
                    Due Date
                    {filters.sortBy === 'paymentDueDate' && (
                      <span className="sort-indicator-v2">
                        {filters.sortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(invoice => (
                  <tr key={`v2-${invoice._id}`}>
                    <td>
                      <span className="invoice-number-v2">{invoice.invoiceNumber}</span>
                    </td>
                    <td>
                      <div className="tutee-info-v2">
                        <span className="tutee-name-v2">{invoice.tutee?.name}</span>
                        <span className="tutee-code-v2">{invoice.tutee?.code}</span>
                      </div>
                    </td>
                    <td>
                      <div className="parent-info-v2">
                        <span className="parent-name-v2">{invoice.parent?.name}</span>
                        <span className="parent-email-v2">{invoice.parent?.email}</span>
                      </div>
                    </td>
                    <td>
                      <span className="invoice-amount-v2">${formatCurrency(invoice.finalAmount)}</span>
                    </td>
                    <td>
                      <span className={`payment-status-badge-v2 payment-${invoice.paymentStatus}`}>
                        {formatPaymentStatus(invoice)}
                      </span>
                    </td>
                    <td>
                      {invoice.financeIC ? (
                        <div className="finance-ic-info-v2">
                          <span className="finance-ic-name-v2">{invoice.financeIC.name}</span>
                          {invoice.assignedAt && (
                            <span className="assignment-date-v2">
                              Assigned: {formatDate(invoice.assignedAt)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="no-assignment-v2">Not assigned</span>
                      )}
                    </td>
                    <td>
                      <span className={`due-date-v2 ${new Date(invoice.paymentDueDate) < new Date() ? 'overdue' : ''}`}>
                        {formatDate(invoice.paymentDueDate)}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons-v2">
                        <button
                          className="btn-icon-v2"
                          onClick={() => handleViewInvoice(invoice)}
                          title="View"
                        >
                          <svg viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <button
                          className="btn-icon-v2"
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowDisputeModal(true);
                          }}
                          title="Disputes"
                        >
                          <svg viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      <InvoiceIcOverrideModal
        isOpen={showAssignmentModal}
        onClose={() => setShowAssignmentModal(false)}
        month={selectedMonth}
        year={selectedYear}
        invoices={invoices}
        onApplied={() => { handleAssignmentComplete(); }}
      />

      {/* Finalize Progress Modal */}
      {finalizeProgress && (
        <div className="modal-overlay" onClick={finalizeProgress.finished ? () => setFinalizeProgress(null) : undefined}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480, padding: '0' }}>
            {/* Header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#893C3B' }}>
                  {finalizeProgress.finished ? 'Finalization Complete' : 'Finalizing Invoices...'}
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                  {finalizeProgress.done} of {finalizeProgress.total} processed
                </p>
              </div>
              {finalizeProgress.finished && (
                <button onClick={() => setFinalizeProgress(null)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>×</button>
              )}
            </div>

            {/* Progress bar */}
            <div style={{ padding: '16px 24px 0' }}>
              <div style={{ background: '#e5e7eb', borderRadius: 999, height: 8, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  borderRadius: 999,
                  background: finalizeProgress.failed > 0 && finalizeProgress.finished ? '#f59e0b' : '#10b981',
                  width: `${Math.round((finalizeProgress.done / finalizeProgress.total) * 100)}%`,
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: '0.78rem', color: '#6b7280' }}>
                <span style={{ color: '#10b981', fontWeight: 600 }}>✓ {finalizeProgress.success} succeeded</span>
                {finalizeProgress.failed > 0 && <span style={{ color: '#ef4444', fontWeight: 600 }}>✗ {finalizeProgress.failed} failed</span>}
                {!finalizeProgress.finished && <span>{finalizeProgress.total - finalizeProgress.done} remaining</span>}
              </div>
            </div>

            {/* Item list */}
            <div style={{ maxHeight: 280, overflowY: 'auto', padding: '12px 24px', margin: '8px 0' }}>
              {finalizeProgress.items.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
                  {item.status === 'ok' ? (
                    <span style={{ color: '#10b981', fontSize: '1rem', flexShrink: 0 }}>✓</span>
                  ) : (
                    <span style={{ color: '#ef4444', fontSize: '1rem', flexShrink: 0 }}>✗</span>
                  )}
                  <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#374151', fontWeight: 600 }}>{item.invoiceNumber}</span>
                  {item.status === 'ok' ? (
                    <span style={{ fontSize: '0.78rem', color: '#10b981' }}>Finalized</span>
                  ) : (
                    <span style={{ fontSize: '0.78rem', color: '#ef4444' }}>{item.error}</span>
                  )}
                </div>
              ))}
              {!finalizeProgress.finished && (
                <div style={{ padding: '8px 0', color: '#9ca3af', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #e5e7eb', borderTopColor: '#893C3B', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                  Processing...
                </div>
              )}
            </div>

            {/* Footer */}
            {finalizeProgress.finished && (
              <div style={{ padding: '12px 24px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setFinalizeProgress(null)}
                  style={{ background: '#893C3B', color: 'white', border: 'none', borderRadius: 8, padding: '8px 24px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' }}
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedInvoice && showInvoicePreviewModal && (
        <div className="modal-overlay" onClick={() => {
          setShowInvoicePreviewModal(false);
          setInvoicePreviewData(null);
        }}>
          <div className="modal-content-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Invoice Preview - {selectedInvoice.invoiceNumber}</h2>
              <button 
                className="modal-close-btn"
                onClick={() => {
                  setShowInvoicePreviewModal(false);
                  setInvoicePreviewData(null);
                }}
                title="Close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {loadingPreview ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <LoadingSpinner />
                </div>
              ) : invoicePreviewData ? (
                <InvoicePreview
                  invoiceType="tutee"
                  recipient={{
                    _id: selectedInvoice.tutee._id,
                    name: selectedInvoice.tutee.name,
                    email: selectedInvoice.tutee?.parent?.email || selectedInvoice.parent?.email,
                    phone: selectedInvoice.tutee?.parent?.phone || selectedInvoice.parent?.phone
                  }}
                  month={selectedInvoice.month}
                  year={selectedInvoice.year}
                  invoiceData={{
                    ...invoicePreviewData,
                    invoiceId: selectedInvoice._id,
                    invoiceNumber: selectedInvoice.invoiceNumber,
                    paymentDueDate: selectedInvoice.paymentDueDate,
                    paymentInstructions: selectedInvoice.paymentInstructions
                      || (selectedInvoice.paymentIC ? {
                        paynowNumber: selectedInvoice.paymentIC.paynowNumber,
                        paynowName: selectedInvoice.paymentIC.paynowName,
                        additionalInstructions: selectedInvoice.paymentIC.additionalInstructions
                      } : null)
                  }}
                  onClose={() => {
                    setShowInvoicePreviewModal(false);
                    setInvoicePreviewData(null);
                  }}
                />
              ) : (
                <p>Failed to load invoice details</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Adjustment Modal - Admin only */}
      {selectedInvoice && showAdjustmentModal && (
        <TuteeInvoiceAdjustmentModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowAdjustmentModal(false);
            setSelectedInvoice(null);
          }}
          onAdjustmentAdded={(updatedInvoice) => {
            setSuccessMessage('Adjustment added successfully!');
            fetchInvoices();
            fetchStats();
          }}
        />
      )}

      {/* Finalize Invoice Modal */}
      {selectedInvoice && showFinalizeModal && (
        <div className="modal-overlay" onClick={() => setShowFinalizeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Finalize Invoice</h2>
              <button className="modal-close-btn" onClick={() => setShowFinalizeModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to finalize invoice <strong>{selectedInvoice.invoiceNumber}</strong>?</p>
              <p style={{ marginTop: '12px', color: '#666', fontSize: '14px' }}>
                Once finalized, the invoice amount and lessons will be locked. You can download the PDF and send it to the parent.
              </p>
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #fbbf24' }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#92400e' }}>
                  <strong>Note:</strong> After finalization, any lesson changes will require creating a revision.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowFinalizeModal(false)} disabled={actionLoading}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleFinalizeInvoice} disabled={actionLoading}>
                {actionLoading ? 'Finalizing...' : 'Finalize Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Sent Modal */}
      {selectedInvoice && showMarkSentModal && (
        <div className="modal-overlay" onClick={() => setShowMarkSentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Mark Invoice as Sent</h2>
              <button className="modal-close-btn" onClick={() => setShowMarkSentModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Confirm that invoice <strong>{selectedInvoice.invoiceNumber}</strong> has been sent to the parent.</p>
              <p style={{ marginTop: '12px', color: '#666', fontSize: '14px' }}>
                This will update the invoice status to "Sent" and mark it as awaiting payment.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowMarkSentModal(false)} disabled={actionLoading}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleMarkAsSent} disabled={actionLoading}>
                {actionLoading ? 'Updating...' : 'Mark as Sent'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Paid Modal */}
      {selectedInvoice && showMarkPaidModal && (
        <div className="modal-overlay" onClick={() => setShowMarkPaidModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Record Payment</h2>
              <button className="modal-close-btn" onClick={() => setShowMarkPaidModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Record payment for invoice <strong>{selectedInvoice.invoiceNumber}</strong></p>

              <div style={{ marginTop: '20px' }}>
                <h4 style={{ margin: '0 0 8px' }}>Payment details (for Mark as fully paid)</h4>
                <div className="form-group">
                  <label>Payment Date:</label>
                  <input
                    type="date"
                    value={paymentData.paymentDate}
                    onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>

                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label>Payment Method:</label>
                  <select
                    value={paymentData.paymentMethod}
                    onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="PayNow">PayNow</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Cash">Cash</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label>Notes (Optional):</label>
                  <textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                    placeholder="Add any payment notes..."
                    rows="3"
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', resize: 'vertical' }}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '12px' }}>
              <div>
                <button className="btn btn-primary" onClick={() => handleMarkAsPaid('verify')} disabled={actionLoading} style={{ width: '100%' }}>
                  {actionLoading ? 'Working...' : 'Send to Payment IC to verify'}
                </button>
                <small style={{ display: 'block', color: '#666', marginTop: '4px' }}>
                  Flags the invoice as payment-indicated and asks the Payment IC to confirm receipt on WhatsApp. Stays unpaid until they verify.
                </small>
              </div>
              <div>
                <button className="btn btn-primary" onClick={() => handleMarkAsPaid('paid')} disabled={actionLoading} style={{ width: '100%' }}>
                  {actionLoading ? 'Working...' : 'Mark as fully paid'}
                </button>
                <small style={{ display: 'block', color: '#666', marginTop: '4px' }}>
                  Marks the invoice paid now and notifies the Payment IC of the updated collected/outstanding totals.
                </small>
              </div>
              <button className="btn btn-secondary" onClick={() => setShowMarkPaidModal(false)} disabled={actionLoading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revise Invoice Modal */}
      {selectedInvoice && showReviseModal && (
        <div className="modal-overlay" onClick={() => { setShowReviseModal(false); setRevisionReason(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Revise Invoice</h3>
              <button className="modal-close-btn" onClick={() => { setShowReviseModal(false); setRevisionReason(''); }}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '12px', color: '#374151', fontSize: '14px' }}>
                This will reset <strong>{selectedInvoice.invoiceNumber}</strong> back to draft and refresh it with all current lessons for <strong>{selectedInvoice.tutee?.name}</strong> — including any new submissions by tutors.
              </p>
              <p style={{ marginBottom: '16px', padding: '10px 12px', background: '#fef3c7', borderRadius: '6px', fontSize: '13px', color: '#92400e', border: '1px solid #fbbf24' }}>
                The same invoice will be updated in-place. Review the refreshed lessons, then re-finalize and re-send to the parent.
              </p>
              <div style={{ marginBottom: '4px' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '6px', color: '#374151' }}>
                  Reason for revision <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  value={revisionReason}
                  onChange={e => setRevisionReason(e.target.value)}
                  placeholder="e.g. Tutor submitted additional lessons after invoice was sent"
                  rows={3}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowReviseModal(false); setRevisionReason(''); }} disabled={actionLoading}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleReviseInvoice}
                disabled={actionLoading || !revisionReason.trim()}
              >
                {actionLoading ? 'Creating revision...' : 'Create Revision'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TuteeInvoiceManagement;